import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2 } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { fetchEscritorio } from '@/api/client';
import {
  compartilharItem,
  deixarDeCompartilhar,
  fetchItemNosEscritorios,
  type ItemNoEscritorio,
  type TipoCompartilhavel,
} from '@/api/compartilhamento';
import { fetchEquipes } from '@/api/equipes';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { useNotification } from '@/context/NotificationProvider';
import { useAuth } from '@/hooks/useAuth';
import { classifyError, notifyMutationError } from '@/lib/errors';
import { queryKeys } from '@/lib/pwa/queryConfig';

import { DestinosPicker, descreverDestinos, useDestinos } from './DestinosPicker';

const NOME_DO_TIPO: Record<TipoCompartilhavel, string> = {
  PROCESSO: 'processo',
  CLIENTE: 'cliente',
  ARTEFATO: 'documento',
  MODELO_PETICAO: 'modelo',
};

interface CompartilharNoEscritorioDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tipo: TipoCompartilhavel;
  recursoId: string;
  /** Como o item aparece no título (o número do processo, o nome do cliente...). */
  rotulo: string;
}

/**
 * "Compartilhar no escritório…" de um item pessoal (docs/PLANO_COMPARTILHAMENTO_ESCRITORIO.md,
 * Bloco 4c): com quem ele fica, deixar de compartilhar (os colegas continuam lendo, A10) e
 * a retirada de vez, que o Proprietário aprova.
 */
export function CompartilharNoEscritorioDialog({
  open,
  onOpenChange,
  tipo,
  recursoId,
  rotulo,
}: CompartilharNoEscritorioDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Compartilhar no escritório</DialogTitle>
          <DialogDescription className="break-words">{rotulo}</DialogDescription>
        </DialogHeader>
        {/* Só montado aberto: cada abertura começa do que o servidor diz. */}
        {open && <Conteudo tipo={tipo} recursoId={recursoId} />}
      </DialogContent>
    </Dialog>
  );
}

function Conteudo({ tipo, recursoId }: { tipo: TipoCompartilhavel; recursoId: string }) {
  const itens = useQuery({
    queryKey: queryKeys.escritorios.item(tipo, recursoId),
    queryFn: () => fetchItemNosEscritorios(tipo, recursoId),
    // A política, a outra aba e a decisão do Proprietário mudam o item sem passar por aqui.
    staleTime: 0,
    refetchOnMount: 'always',
  });
  const [escolhido, setEscolhido] = useState<string | null>(null);

  if (itens.isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Spinner size={32} label="Carregando" />
      </div>
    );
  }
  if (itens.error || !itens.data) {
    return (
      <Alert variant="destructive">
        <AlertDescription>{classifyError(itens.error).message}</AlertDescription>
      </Alert>
    );
  }
  if (itens.data.length === 0) {
    return (
      <div className="space-y-3 text-sm">
        <p className="text-muted-foreground">Você ainda não faz parte de um escritório.</p>
        <Button asChild variant="outline">
          <Link to="/app/escritorios">
            <Building2 className="mr-2 h-4 w-4" />
            Ir para Escritórios
          </Link>
        </Button>
      </div>
    );
  }
  const item = itens.data.find((i) => i.escritorio_id === escolhido) ?? itens.data[0];
  return (
    <div className="space-y-4">
      {itens.data.length > 1 && (
        <Select value={item.escritorio_id} onValueChange={setEscolhido}>
          <SelectTrigger className="h-11 sm:h-9" aria-label="Escritório">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {itens.data.map((i) => (
              <SelectItem key={i.escritorio_id} value={i.escritorio_id}>
                {i.escritorio_nome}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      <PainelDoEscritorio
        // Depois de cada troca, o painel renasce do que o servidor gravou.
        key={`${item.escritorio_id}:${item.estado}:${item.alvos.map((a) => `${a.tipo}${a.valor}`).join(',')}`}
        tipo={tipo}
        recursoId={recursoId}
        item={item}
      />
    </div>
  );
}

function PainelDoEscritorio({
  tipo,
  recursoId,
  item,
}: {
  tipo: TipoCompartilhavel;
  recursoId: string;
  item: ItemNoEscritorio;
}) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { showSuccess, showError } = useNotification();
  const escId = item.escritorio_id;
  const nome = NOME_DO_TIPO[tipo];
  const pelaRegra = item.estado === 'POLITICA' && item.incluir_proximos;

  const detalhe = useQuery({ queryKey: queryKeys.escritorios.detail(escId), queryFn: () => fetchEscritorio(escId) });
  const equipes = useQuery({ queryKey: queryKeys.escritorios.equipes(escId), queryFn: () => fetchEquipes(escId) });
  const membros = detalhe.data?.membros ?? [];
  const colegas = membros.filter((m) => m.usuario_id !== user?.id);

  const iniciais =
    item.estado === 'NAO'
      ? item.alvos_da_politica.length > 0
        ? item.alvos_da_politica
        : [{ tipo: 'ESCRITORIO' as const }]
      : item.alvos;
  const { alvos, alternar, escolhidos, mudou } = useDestinos(iniciais);

  const atualizar = (novo: ItemNoEscritorio) => {
    queryClient.setQueryData<ItemNoEscritorio[]>(queryKeys.escritorios.item(tipo, recursoId), (lista) =>
      lista?.map((i) => (i.escritorio_id === novo.escritorio_id ? novo : i)),
    );
    // A política pode ter virado "Só os que eu escolher", e quem vê o quê mudou.
    for (const chave of [
      queryKeys.escritorios.compartilhamento(escId),
      queryKeys.escritorios.pedidos(escId),
      queryKeys.processos.all,
      queryKeys.comunicacoes.all,
      queryKeys.clientes.all,
      queryKeys.documentos.all,
      queryKeys.modelos.all,
    ]) {
      queryClient.invalidateQueries({ queryKey: chave });
    }
  };

  const compartilhar = useMutation({
    mutationFn: () => compartilharItem(escId, tipo, recursoId, escolhidos),
    onSuccess: (novo) => {
      showSuccess(`Compartilhado com ${descreverDestinos(novo.alvos, equipes.data ?? [], membros)}`);
      atualizar(novo);
    },
    onError: (err) => notifyMutationError(err, showError),
  });
  const deixar = useMutation({
    mutationFn: () => deixarDeCompartilhar(escId, tipo, recursoId),
    onSuccess: (novo) => {
      showSuccess('Os colegas deixaram de ver');
      atualizar(novo);
    },
    onError: (err) => notifyMutationError(err, showError),
  });
  const ocupado = compartilhar.isPending || deixar.isPending;

  const destinos = descreverDestinos(item.alvos, equipes.data ?? [], membros) || 'ninguém';
  const situacao = {
    NAO: `Só você vê este ${nome} neste escritório.`,
    POLITICA: pelaRegra
      ? `Compartilhado pela sua preferência "Todos, inclusive os que chegarem", com ${destinos}.`
      : `Compartilhado com ${destinos} (pela sua escolha "Todos os que tenho hoje").`,
    ITEM: `Compartilhado com ${destinos}.`,
    RETIDO: `Deixou de ser compartilhado. Quem via (${destinos}) continua só lendo.`,
  }[item.estado];
  const rotuloPrincipal =
    item.estado === 'NAO' ? 'Compartilhar' : item.estado === 'RETIDO' ? 'Compartilhar de novo' : 'Salvar';
  const precisaMudar = item.estado === 'ITEM' || item.estado === 'POLITICA';

  return (
    <div className="space-y-4">
      <p className="rounded-md border bg-muted/30 p-3 text-sm">{situacao}</p>

      {pelaRegra ? (
        <p className="text-xs text-muted-foreground">
          Para mudar com quem, use a aba{' '}
          <Link className="underline" to={`/app/escritorios/${escId}?aba=compartilhamento`}>
            Compartilhamento
          </Link>{' '}
          do escritório.
        </p>
      ) : detalhe.isLoading || equipes.isLoading ? (
        <div className="flex justify-center py-4">
          <Spinner size={32} label="Carregando os colegas" />
        </div>
      ) : (
        <DestinosPicker
          id={`com-quem-item-${escId}`}
          titulo="Com quem"
          alvos={alvos}
          onAlternar={alternar}
          equipes={equipes.data ?? []}
          colegas={colegas}
        />
      )}

      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:flex-wrap sm:justify-end">
        {(item.estado === 'ITEM' || item.estado === 'POLITICA') && (
          <Button className="h-11 sm:h-9" variant="outline" onClick={() => deixar.mutate()} disabled={ocupado}>
            {deixar.isPending && <Spinner size={16} className="mr-2 text-inherit" />}
            Deixar de compartilhar
          </Button>
        )}
        {!pelaRegra && (
          <Button
            className="h-11 sm:h-9"
            onClick={() => compartilhar.mutate()}
            disabled={ocupado || escolhidos.length === 0 || (precisaMudar && !mudou(item.alvos))}
          >
            {compartilhar.isPending && <Spinner size={16} className="mr-2 text-inherit" />}
            {rotuloPrincipal}
          </Button>
        )}
      </div>
    </div>
  );
}
